import { IonContent, IonPage } from "@ionic/react";
import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { FormControl } from 'baseui/form-control';
import { Check, Delete } from "baseui/icon";
import { DURATION, useSnackbar } from "baseui/snackbar";
import { DisplayLarge, HeadingXLarge, ParagraphLarge, ParagraphSmall } from "baseui/typography";
import { forwardRef, useRef } from "react";
import { useDispatch } from 'react-redux';
import { forgotPassword, resetPassword } from "../../application/actions/auth";
import { routeTo } from "../../application/actions/ui";
import useWhoAmI from "../../hooks/useWhoAmI";
import useLocale from "../../hooks/useLocale";
import ReDoBanner from "../../components/ReDoBanner";
import ReDoButton from "../../components/ReDoButton";
import ReDoInput from "../../components/ReDoInput";
import ReDoLogo from "../../components/ReDoLogo";
import ReDoBackButton from "../../components/ReDoBackButton";


const ForgotPassword = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const { enqueue, dequeue } = useSnackbar();
  const formRef = useRef()
  const { isCompany, isFederated } = useWhoAmI()
  const { t } = useLocale()

  const column = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    maxWidth: '370px',
    margin: '0 auto',
  });

  const onBack = () => {
    if (props.settings) {
      dispatch(routeTo({ view: isCompany ? 'companyProfile' : 'userProfile' }))
    } else {
      dispatch(routeTo({ view: 'login' }))
    }
  }

  const onSendCode = (e) => {
    e.preventDefault();
    const email = e.target.email.value.toLowerCase();
    dispatch(forgotPassword({ username: email }))
      .then(r => {
        const dest = r?.CodeDeliveryDetails?.Destination;
        const message = dest
          ? t('auth.forgot_toast_code_sent', { dest })
          : t('auth.forgot_toast_code_fallback');
        enqueue({ message, dismissAfter: DURATION.medium, startEnhancer: () => <Check size={theme.sizing.scale800} /> });
      })
      .catch(e => {
        enqueue({ message: e.message, dismissAfter: DURATION.medium, startEnhancer: () => <Delete size={theme.sizing.scale800} /> });
      });
  }

  const onResetPassword = (e) => {
    e.preventDefault();
    const email = formRef.current['email'].value.toLowerCase();
    const code = e.target.code.value;
    const password = e.target.password.value;
    enqueue({ message: t('auth.forgot_toast_resetting'), progress: true }, DURATION.infinite)
    dispatch(resetPassword({ username: email, code, password }))
      .then(r => {
        dequeue()
        enqueue({ message: t('auth.forgot_toast_reset_success'), dismissAfter: DURATION.shortest, startEnhancer: () => <Check size={theme.sizing.scale800} /> });
        if (props.settings) {
          dispatch(routeTo({ view: isCompany ? 'companyProfile' : 'userProfile' }))
        } else {
          dispatch(routeTo({ view: 'login' }))
        }
      })
      .catch(e => {
        dequeue()
        enqueue({ message: e.message, dismissAfter: DURATION.medium, startEnhancer: () => <Delete size={theme.sizing.scale800} /> });
      });
  }

  const onSignUp = () => {
    dispatch(routeTo({ view: 'signup' }))
  }

  return (
    <IonPage ref={ref} >
      <IonContent className="ion-padding">
        <Block display={['flex', 'flex', 'flex', 'none']} marginBottom='1ch'>
          <ReDoBackButton onClick={onBack} />
        </Block>
        <FlexGrid
          flexGridColumnCount={[1, 1, 1, 2]}
          flexGridColumnGap="scale800"
          flexGridRowGap="scale800"
          height='100%'
          alignItems='center'
        >
          <FlexGridItem position='relative' height='100%' display={['none', 'none', 'none', 'flex']}>
            <div className={css({ position: 'fixed', top: '50px', right: '75px' })}>
              <ReDoLogo size={theme.sizing.scale2400} />
            </div>
            <ReDoBanner>
              <ReDoBackButton onClick={onBack} />
              <div className={css({ height: '100%', justifyContent: 'space-between', display: 'flex', flexDirection: 'column', padding: `${theme.sizing.scale1600} ${theme.sizing.scale3200}` })}>

                <div className={css({ paddingTop: theme.sizing.scale2400 })}>

                  <DisplayLarge>{!props.settings ? t('auth.forgot_banner_heading_default') : t('auth.forgot_banner_heading_settings')}</DisplayLarge>
                </div>
                {!props.settings && <div className={css({ gap: '2ch', display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' })}>
                  <ParagraphLarge>{t('auth.forgot_banner_no_account')}</ParagraphLarge>
                  <Block width={theme.sizing.scale3200}>
                    <ReDoButton
                      kind='outline'
                      onClick={onSignUp}
                      overrides={{ BaseButton: { style: { paddingTop: '0.5ch', paddingBottom: '0.5ch' } } }}
                    >
                      {t('auth.forgot_banner_btn_signup')}
                    </ReDoButton>
                  </Block>
                </div>}
              </div>
            </ReDoBanner>
          </FlexGridItem>

          <FlexGridItem height='100%'>
            <div className={column}>
              <Block alignSelf='flex-end' display={['unset', 'unset', 'unset', 'none']}>
                <ReDoLogo size={theme.sizing.scale1600} />
              </Block>
              <HeadingXLarge marginTop='auto' marginRight='2px' marginLeft='2px' display={['flex', 'flex', 'flex', 'none']}>{props.settings ? t('auth.forgot_heading_mobile_settings') : t('auth.forgot_heading_mobile_default')}</HeadingXLarge>
              <Block paddingBottom='28px' marginBottom={['auto', 'auto', 'auto', '0']} marginTop={['5%', '5%', '5%', '70%']}>
                {props.settings && isFederated && (
                  <Block backgroundColor={theme.colors.warning100} padding='1ch 1.5ch' marginBottom='2ch' overrides={{ Block: { style: { borderRadius: '8px' } } }}>
                    <ParagraphSmall margin='0' color={theme.colors.warning600}>
                      {t('auth.forgot_federated_warning')}
                    </ParagraphSmall>
                  </Block>
                )}
                <form ref={formRef} onSubmit={onSendCode}>
                  <div className={css({ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: theme.sizing.scale400 })}>
                    <FormControl overrides={{ ControlContainer: { style: { marginBottom: 0 } } }}>
                      <ReDoInput
                        id="email-input"
                        type="email"
                        placeholder={t('auth.forgot_input_email')}
                        name='email'
                        required
                      />
                    </FormControl>
                    <div className={css({ textAlign: 'center', margin: '12px 0', marginLeft: 'auto' })}>
                      <ReDoButton kind='link' color={theme.colors.primary300} type="submit" overrides={{ BaseButton: { style: { marginBottom: theme.sizing.scale400 } } }}
                      >
                        {t('auth.forgot_btn_send_code')}
                      </ReDoButton>
                    </div>
                  </div>
                </form>
                <form onSubmit={onResetPassword}>
                  <div className={css({ marginBottom: 'auto', alignItems: 'center', display: 'flex', flexDirection: 'column', gap: theme.sizing.scale400 })}>
                    <FormControl overrides={{ ControlContainer: { style: { marginBottom: 0 } } }}>
                      <ReDoInput
                        id="code-input"
                        placeholder={t('auth.forgot_input_code')}
                        name='code'
                        required
                      />
                    </FormControl>
                    <FormControl overrides={{ ControlContainer: { style: { marginBottom: 0 } } }}>
                      <ReDoInput
                        id="password-input"
                        type="password"
                        placeholder={t('auth.forgot_input_new_password')}
                        name='password'
                        required
                      />
                    </FormControl>
                    <Block marginTop='15%' width={['100%', '100%', '100%', '320px']}>
                      <ReDoButton kind='brand' type="submit">
                        {t('auth.forgot_btn_reset')}
                      </ReDoButton>
                    </Block>
                  </div>
                </form>
              </Block>
            </div>
          </FlexGridItem>
        </FlexGrid>
      </IonContent>
    </IonPage>
  );
});

export default ForgotPassword;
